/* eslint-disable react-hooks/exhaustive-deps */
import { Fragment, useEffect, useState } from 'react';
import {
  Autocomplete,
  Box,
  Container,
  IconButton,
  Pagination,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  alpha,
  styled,
  useMediaQuery,
} from '@mui/material';
import { Trans, useTranslation } from 'react-i18next';
import {
  Clear,
  ImageNotSupportedOutlined,
  KeyboardArrowDown,
  Loop,
  Search,
} from '@mui/icons-material';
import MuiAccordion from '@mui/material/Accordion';
import MuiAccordionSummary from '@mui/material/AccordionSummary';
import MuiAccordionDetails from '@mui/material/AccordionDetails';
import { logEvent } from 'firebase/analytics';

import PageTitle from '../../components/PageTitle/PageTitle';
import { CustomTextField, StyledButton } from '../../components/Utils/UIUtils';
import { theme } from '../../theme';
import { useDispatch, useSelector } from 'react-redux';
import { fetchChannels } from '../../actions/channelActions';
import countries from '../../data/countries.json';
import categories from '../../data/categories.json';
import languages from '../../data/languages.json';
import { MyHelmet } from './MyHelmet';
import { analytics } from '../../config/firebase';
import { capitalizeFirstLetter } from '../../components/Utils/Formating';

const CustomChip = ({ handleDelete, text }) => {
  return (
    <Box
      sx={{
        borderRadius: theme.shape.secondaryBorderRadius,
        backgroundColor: alpha(theme.palette.primary.neutral100, 0.3),
        border: '1px solid',
        borderColor: 'primary.neutral300',
        px: '10px',
        py: '5px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 1,
        minHeight: '22px',
        height: '28px',
      }}
    >
      <Typography
        variant="body"
        fontSize={'12px'}
        sx={{ color: 'primary.neutral800' }}
      >
        {text}
      </Typography>
      <Clear
        sx={{
          color: 'primary.neutral800',
          cursor: 'pointer',
          fontSize: '16px',
        }}
        onClick={() => handleDelete([])}
      />
    </Box>
  );
};

const Accordion = styled((props) => (
  <MuiAccordion
    disableGutters
    elevation={0}
    square
    {...props}
    TransitionProps={{ unmountOnExit: true }}
  />
))(({ theme }) => ({
  backgroundColor: 'transparent',
  border: '1px solid',
  borderColor: theme.palette.primary.neutral200,
  borderRadius: theme.shape.defaultBorderRadius,
  '&::before': {
    display: 'none',
  },
}));

const AccordionSummary = styled((props) => (
  <MuiAccordionSummary
    expandIcon={
      <KeyboardArrowDown
        sx={{ fontSize: '1.5rem', color: 'primary.neutral800' }}
      />
    }
    {...props}
  />
))(({ theme }) => ({
  flexDirection: 'row',
  '& .MuiAccordionSummary-root:not(:last-child)': {
    borderBottom: 0,
  },
  '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
    transform: 'rotate(180deg)',
  },
  '& .MuiAccordionSummary-content': {
    marginLeft: theme.spacing(1),
  },
  '& .MuiTypography-root': {
    fontWeight: '500',
  },
}));

const AccordionDetails = styled(MuiAccordionDetails)(() => ({
  padding: 0,
  '& .MuiTypography-root': {
    fontWeight: '400',
  },
}));

const Row = ({ channel }) => {
  const isMobile = useMediaQuery(theme.breakpoints.only('xs'));
  return (
    <TableRow>
      <TableCell>
        {channel.imageName ? (
          <img
            src={`https://firebasestorage.googleapis.com/v0/b/slex-24459.appspot.com/o/channels_logos%2F${channel.imageName}?alt=media`}
            alt={channel.name}
            loading="lazy"
            referrerPolicy="no-referrer"
            style={{
              maxWidth: isMobile ? '5rem' : '7rem',
              maxHeight: isMobile ? '2.5rem' : '3rem',
              verticalAlign: 'middle',
            }}
          />
        ) : (
          <Box
            sx={{
              width: '7rem',
              height: '3rem',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              backgroundColor: alpha(theme.palette.primary.neutral800, 0.1),
              borderRadius: theme.shape.secondaryBorderRadius,
            }}
          >
            <ImageNotSupportedOutlined
              sx={{
                color: 'primary.neutral500',
              }}
            />
          </Box>
        )}
      </TableCell>
      <TableCell>
        <Stack direction={'column'}>
          <Typography variant="body1">{channel.name}</Typography>
          <Typography
            variant="body2"
            color="primary.neutral500"
            sx={{
              fontSize: '14px',
            }}
          >
            {channel.categories.length > 0
              ? channel.categories
                  .map((category) => capitalizeFirstLetter(category))
                  .join(', ')
              : 'No categories'}
          </Typography>
        </Stack>
      </TableCell>
      <TableCell>
        <Typography
          variant="body1"
          sx={{
            bgcolor: 'primary.neutral400',
            width: 'fit-content',
            px: 1,
            py: 0.5,
            borderRadius: theme.shape.chipBorderRadius,
            color: 'white',
            fontWeight: '400',
            fontSize: '14px',
          }}
        >
          {channel.altId}
        </Typography>
      </TableCell>
    </TableRow>
  );
};

export default function Channels() {
  const { t } = useTranslation();

  const [page, setPage] = useState(1);
  const pageSize = 256;
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [selectedCountries, setSelectedCountries] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedLanguages, setSelectedLanguages] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');

  const handleCountryChange = (event, value) => {
    setSelectedCountries(value);
  };

  const handleCategoryChange = (event, value) => {
    setSelectedCategories(value);
  };

  const handleLanguageChange = (event, value) => {
    setSelectedLanguages(value);
  };

  const handleSearchQueryChange = (event) => {
    setSearchQuery(event.target.value);
  };

  const handleCountryDelete = (index) => {
    const newSelectedCountries = [...selectedCountries];
    newSelectedCountries.splice(index, 1);
    setSelectedCountries(newSelectedCountries);
  };

  const handleCategoryDelete = (index) => {
    const newSelectedCategories = [...selectedCategories];
    newSelectedCategories.splice(index, 1);
    setSelectedCategories(newSelectedCategories);
  };

  const handleLanguageDelete = (index) => {
    const newSelectedLanguages = [...selectedLanguages];
    newSelectedLanguages.splice(index, 1);
    setSelectedLanguages(newSelectedLanguages);
  };

  const handleResetFilters = () => {
    if (
      selectedCountries.length > 0 ||
      selectedCategories.length > 0 ||
      selectedLanguages.length > 0
    ) {
      setSelectedCountries([]);
      setSelectedCategories([]);
      setSelectedLanguages([]);
    }
  };

  const channelsData = useSelector((state) => state.channels);

  const [expanded, setExpanded] = useState();

  const handleChange = (panel) => (event, newExpanded) => {
    setExpanded(newExpanded ? panel : false);
  };

  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(
      fetchChannels(
        searchQuery,
        selectedCategories,
        selectedCountries,
        selectedLanguages,
        1,
        pageSize
      )
    );
    setPage(1);
  }, [selectedCategories, selectedCountries, selectedLanguages, dispatch]);

  const submitSearch = (e) => {
    e.preventDefault();
    setPage(1);
    dispatch(
      fetchChannels(
        searchQuery,
        selectedCategories,
        selectedCountries,
        selectedLanguages,
        1,
        pageSize
      )
    );
  };

  const handlePageChange = (event, value) => {
    const page = value;
    setPage(page);
    dispatch(
      fetchChannels(
        searchQuery,
        selectedCategories,
        selectedCountries,
        selectedLanguages,
        page,
        pageSize
      )
    );
  };

  // Group channels by country
  const groupedChannels = channelsData.channels.reduce((acc, channel) => {
    const countryCode = channel.country;
    if (!acc[countryCode]) {
      acc[countryCode] = [];
    }
    acc[countryCode].push(channel);
    return acc;
  }, {});

  Object.values(groupedChannels).forEach((channels) => {
    channels.sort((a, b) => a.country.localeCompare(b.country));
  });

  // Firebase analytics
  useEffect(() => {
    logEvent(analytics, 'page_view', {
      page_title: 'Channels',
      page_location: window.location.href,
      page_path: window.location.pathname,
    });
  }, []);
  return (
    <>
      <MyHelmet />
      <PageTitle>
        <Stack
          component={'header'}
          gap={2}
          justifyContent="center"
          textAlign={'center'}
        >
          <Typography
            variant="h1"
            color="primary.neutral800"
            sx={{
              fontSize: '32px',
              fontWeight: '700',
              textTransform: 'uppercase',
            }}
          >
            {t('CHANNELS.TITLE')}
          </Typography>
          <Typography
            variant="body1"
            color="primary.neutral500"
            sx={{ width: { xs: '100%', md: '60%' }, mx: 'auto' }}
          >
            {t('CHANNELS.DESCRIPTION')}
          </Typography>
        </Stack>
      </PageTitle>
      <Container
        maxWidth={'lg'}
        sx={{
          py: '40px',
          minHeight: '50vh',
        }}
      >
        <Stack
          sx={{
            display: 'flex',
            justifyContent: 'center',
            flexDirection: 'column',
            gap: 10,
          }}
        >
          <Stack
            sx={{
              width: '100%',
              mx: 'auto',
              border: '1px solid',
              borderColor: 'primary.neutral200',
              p: 3,
              borderRadius: theme.shape.defaultBorderRadius,
            }}
            gap={2}
          >
            <Stack
              sx={{ gap: 4, alignItems: { xs: 'flex-start', md: 'center' } }}
              direction={{ xs: 'column', md: 'row' }}
            >
              <Typography
                variant="h2"
                color="primary.neutral800"
                fontSize={'20px'}
                fontWeight={'500'}
              >
                {t('CHANNELS.CHANNEL_TITLE')}
              </Typography>
              <Stack direction={'row'} sx={{ width: '100%' }} gap={1}>
                <CustomTextField
                  placeholder={t('CHANNELS.CHANNEL_SEARCH_INPUT_PLACEHOLDER')}
                  value={searchQuery}
                  onChange={handleSearchQueryChange}
                  onSubmit={submitSearch}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      submitSearch(e);
                    }
                  }}
                  InputProps={{
                    startAdornment: (
                      <Search
                        sx={{
                          p: 0.25,
                          color: 'primary.neutral400',
                          ml: -0.5,
                          mr: 1,
                        }}
                        position="start"
                      ></Search>
                    ),
                  }}
                  sx={{
                    '& .MuiInputBase-input': {
                      pl: 0,
                    },
                  }}
                />
                <StyledButton
                  variant="contained"
                  sx={{
                    height: '44px',
                    px: 3,
                    bgcolor: 'primary.main',
                    color: 'white',
                    '&:hover': {
                      bgcolor: alpha(theme.palette.primary.main, 0.8),
                    },
                  }}
                  onClick={submitSearch}
                >
                  {t('CHANNELS.SEARCH_BUTTON')}
                </StyledButton>
              </Stack>
            </Stack>
            <Stack
              direction={'row'}
              gap={2}
              alignItems={'center'}
              flexWrap={'wrap'}
            >
              <Stack gap={1}>
                <Typography
                  variant="body1"
                  color="primary.neutral800"
                  fontSize={'14px'}
                >
                  {t('CHANNELS.COUNTRY')}
                </Typography>
                <Autocomplete
                  id="selected-countries"
                  options={countries.map((country) => country)}
                  getOptionLabel={(option) => option.name}
                  value={selectedCountries.map((country) => country)}
                  disableCloseOnSelect
                  multiple
                  onChange={handleCountryChange}
                  sx={{
                    '& .MuiInputBase-root': {
                      py: '2.5px',
                      fontSize: '14px',
                    },
                    minWidth: '150px',
                  }}
                  renderInput={(params) => (
                    <CustomTextField
                      {...params}
                      placeholder={
                        selectedCountries.length === 0
                          ? t('CHANNELS.COUNTRY_SELECT_PLACEHOLDER')
                          : ''
                      }
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <Stack
                            sx={{
                              display: 'flex',
                              gap: 1,
                              flexDirection: 'row',
                              alignItems: 'center',
                            }}
                          >
                            {selectedCountries
                              .slice(0, 1)
                              .map((country, index) => (
                                <Fragment key={index}>
                                  <CustomChip
                                    handleDelete={handleCountryDelete}
                                    text={country.name}
                                  />
                                </Fragment>
                              ))}
                            {selectedCountries.length > 1 && (
                              <Typography
                                variant="body"
                                fontSize={'14px'}
                                sx={{ color: 'primary.neutral700' }}
                              >
                                +{selectedCountries.length - 1}
                              </Typography>
                            )}
                          </Stack>
                        ),
                      }}
                    />
                  )}
                />
              </Stack>
              <Stack gap={1}>
                <Typography
                  variant="body1"
                  color="primary.neutral800"
                  fontSize={'14px'}
                >
                  {t('CHANNELS.CATEGORY')}
                </Typography>
                <Autocomplete
                  id="selected-categories"
                  options={categories.map((category) => category)}
                  getOptionLabel={(category) => category.name}
                  value={selectedCategories.map((category) => category)}
                  disableCloseOnSelect
                  multiple
                  onChange={handleCategoryChange}
                  sx={{
                    '& .MuiInputBase-root': {
                      py: '2.5px',
                      fontSize: '14px',
                    },
                    minWidth: '150px',
                  }}
                  renderInput={(params) => (
                    <CustomTextField
                      {...params}
                      placeholder={
                        selectedCategories.length === 0
                          ? t('CHANNELS.CATEGORY_SELECT_PLACEHOLDER')
                          : ''
                      }
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <Stack
                            sx={{
                              display: 'flex',
                              gap: 1,
                              flexDirection: 'row',
                              alignItems: 'center',
                            }}
                          >
                            {selectedCategories
                              .slice(0, 1)
                              .map((category, index) => (
                                <Fragment key={index}>
                                  <CustomChip
                                    handleDelete={handleCategoryDelete}
                                    text={category.name}
                                  />
                                </Fragment>
                              ))}
                            {selectedCategories.length > 1 && (
                              <Typography
                                variant="body"
                                fontSize={'14px'}
                                sx={{ color: 'primary.neutral700' }}
                              >
                                +{selectedCategories.length - 1}
                              </Typography>
                            )}
                          </Stack>
                        ),
                      }}
                    />
                  )}
                />
              </Stack>
              <Stack gap={1}>
                <Typography
                  variant="body1"
                  color="primary.neutral800"
                  fontSize={'14px'}
                >
                  {t('CHANNELS.LANGUAGE')}
                </Typography>
                <Autocomplete
                  id="selected-languages"
                  options={languages.map((language) => language)}
                  getOptionLabel={(language) => language.nativeName}
                  value={selectedLanguages.map((language) => language)}
                  disableCloseOnSelect
                  multiple
                  onChange={handleLanguageChange}
                  sx={{
                    '& .MuiInputBase-root': {
                      py: '2.5px',
                      fontSize: '14px',
                    },
                    minWidth: '150px',
                  }}
                  renderInput={(params) => (
                    <CustomTextField
                      {...params}
                      placeholder={
                        selectedLanguages.length === 0
                          ? t('CHANNELS.LANGUAGE_SELECT_PLACEHOLDER')
                          : ''
                      }
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <Stack
                            sx={{
                              display: 'flex',
                              gap: 1,
                              flexDirection: 'row',
                              alignItems: 'center',
                            }}
                          >
                            {selectedLanguages
                              .slice(0, 1)
                              .map((language, index) => (
                                <Fragment key={index}>
                                  <CustomChip
                                    handleDelete={handleLanguageDelete}
                                    text={language.nativeName}
                                  />
                                </Fragment>
                              ))}
                            {selectedLanguages.length > 1 && (
                              <Typography
                                variant="body"
                                fontSize={'14px'}
                                sx={{ color: 'primary.neutral700' }}
                              >
                                +{selectedLanguages.length - 1}
                              </Typography>
                            )}
                          </Stack>
                        ),
                      }}
                    />
                  )}
                />
              </Stack>
              <Stack
                gap={1}
                direction={'row'}
                sx={{
                  alignSelf: 'flex-end',
                }}
              >
                {isMobile ? (
                  <IconButton
                    variant="contained"
                    sx={{
                      height: '44px',
                      width: '44px',
                      bgcolor: alpha(theme.palette.primary.main, 0.1),
                      borderRadius: theme.shape.defaultBorderRadius,
                      color: 'primary.main',
                      '&:hover': {
                        bgcolor: alpha(theme.palette.primary.main, 0.2),
                      },
                    }}
                    onClick={handleResetFilters}
                  >
                    <Loop />
                  </IconButton>
                ) : (
                  <StyledButton
                    variant="contained"
                    sx={{
                      height: '44px',
                      px: 2,
                      bgcolor: 'transparent',
                      color: 'primary.main',
                      '&:hover': {
                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                      },
                    }}
                    onClick={handleResetFilters}
                    startIcon={<Loop />}
                  >
                    {t('CHANNELS.RESET_FILTERS')}
                  </StyledButton>
                )}
              </Stack>
            </Stack>
            <Typography
              variant="body1"
              color="primary.neutral500"
              sx={{ fontSize: '14px' }}
            >
              <Trans
                i18nKey="CHANNELS.CHANNELS_COUNT"
                values={{
                  count: channelsData.totalChannels,
                }}
              />
            </Typography>
            <Stack gap={1.5}>
              {Object.entries(groupedChannels).map(
                ([countryCode, channels], index) => (
                  <Accordion
                    key={index}
                    expanded={expanded === `panel${index + 1}`}
                    onChange={handleChange(`panel${index + 1}`)}
                  >
                    <AccordionSummary
                      aria-controls={`panel${index + 1}d-content`}
                      id={`panel${index + 1}d-header`}
                      sx={{
                        '& .MuiTypography-root': {
                          fontSize: '16px',
                        },
                      }}
                    >
                      <Typography variant="body1">
                        {countries.find((c) => c.code === countryCode)?.name ||
                          t('GENERAL.UNKNOWN')}
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails
                      sx={{
                        overflow: 'auto',
                      }}
                    >
                      <Table>
                        <TableHead>
                          <TableRow
                            sx={{
                              backgroundColor: alpha(
                                theme.palette.primary.primarylight1,
                                0.3
                              ),
                              borderTop: '1px solid',
                              borderBottom: '1px solid',
                              borderColor: 'primary.neutral100',
                              '& .MuiTableCell-root': {
                                borderBottom: 'none',
                                py: 1,
                              },
                              '& .MuiTypography-root': {
                                fontSize: '13px',
                                color: 'primary.neutral550',
                                fontWeight: '500',
                              },
                            }}
                          >
                            <TableCell style={{ width: '14%' }}></TableCell>
                            <TableCell>
                              <Typography variant="body1">NAME</Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body1">TVG-ID</Typography>
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody
                          sx={{
                            '& tr:last-child td': {
                              borderBottom: 0,
                            },
                            '& td': {
                              py: 2.5,
                            },
                          }}
                        >
                          {channels.map((channel, index) => (
                            <Row key={index} channel={channel} />
                          ))}
                        </TableBody>
                      </Table>
                    </AccordionDetails>
                  </Accordion>
                )
              )}
            </Stack>
            <Pagination
              count={channelsData.totalPages || 1}
              color="primary"
              variant="outlined"
              shape="rounded"
              page={page}
              onChange={handlePageChange}
              sx={{
                alignSelf: 'center',
                '& .MuiPaginationItem-root': {
                  borderRadius: theme.shape.defaultBorderRadius,
                  borderColor: theme.palette.primary.neutral200,
                },
              }}
            />
          </Stack>
        </Stack>
      </Container>
    </>
  );
}
